import { useEffect, useState } from "react";
import { api, getSiteToken, setSiteToken, getToken, clearSiteToken } from "../lib/api";
import { Lock, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";

/**
 * Wraps the app in a site-wide password gate. If a valid token is already
 * stored (visitor or admin), we let the user through without any check.
 * Otherwise we render a centered password form.
 */
export default function SiteGate({ children }) {
    const [checking, setChecking] = useState(true);
    const [unlocked, setUnlocked] = useState(false);
    const [requiresUnlock, setRequiresUnlock] = useState(true);
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/site/status");
                if (cancelled) return;
                setRequiresUnlock(!!data.requires_unlock);
                if (!data.requires_unlock) {
                    setUnlocked(true);
                    return;
                }
                const stored = getSiteToken() || getToken();
                if (!stored) {
                    setUnlocked(false);
                    return;
                }
                // Validate by calling a protected endpoint
                try {
                    await api.get("/categories");
                    setUnlocked(true);
                } catch (err) {
                    if (err?.response?.status === 401) {
                        clearSiteToken();
                        setUnlocked(false);
                    } else {
                        setUnlocked(true); // network blip — let them in optimistically
                    }
                }
            } catch (err) {
                console.error(err);
                setUnlocked(true); // fail open on network errors so we don't lock people out
            } finally {
                if (!cancelled) setChecking(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        setError("");
        try {
            const { data } = await api.post("/site/unlock", { password });
            setSiteToken(data.token);
            setUnlocked(true);
            toast.success("Welcome");
        } catch (err) {
            setError(err?.response?.data?.detail || "Could not unlock");
        } finally {
            setSubmitting(false);
        }
    };

    if (checking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-medical-50">
                <Loader2 className="h-5 w-5 animate-spin text-teal-700" />
            </div>
        );
    }

    if (!requiresUnlock || unlocked) {
        return children;
    }

    return (
        <div
            className="min-h-screen flex items-center justify-center px-6 bg-medical-50 relative"
            data-testid="site-gate"
        >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,#CCFBF1_0%,transparent_45%),radial-gradient(circle_at_85%_85%,#F1F5F9_0%,transparent_45%)] opacity-70 pointer-events-none" />
            <div className="relative w-full max-w-sm">
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
                    <div className="flex items-center gap-2 text-teal-700 mb-5">
                        <BookOpen className="h-5 w-5" />
                        <span className="font-display text-sm font-bold">ED Whiteboard</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-teal-700 mb-2">
                        <Lock className="h-3 w-3" />
                        Private resource
                    </div>
                    <h1 className="font-display text-2xl font-black text-medical-900 mb-1 tracking-tight">
                        Enter site password
                    </h1>
                    <p className="text-sm text-slate-500 mb-6">
                        This handbook is for internal Emergency Department use only.
                    </p>

                    <form onSubmit={submit} className="space-y-3">
                        <input
                            data-testid="site-gate-password-input"
                            type="password"
                            autoFocus
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Password"
                            required
                            className="w-full h-11 px-3 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-600 font-mono"
                        />
                        {error && (
                            <div
                                data-testid="site-gate-error"
                                className="text-xs text-red-600"
                            >
                                {error}
                            </div>
                        )}
                        <button
                            data-testid="site-gate-submit"
                            type="submit"
                            disabled={submitting}
                            className="w-full h-11 inline-flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 text-white rounded-md font-medium transition-colors disabled:opacity-60"
                        >
                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            Unlock
                        </button>
                    </form>
                </div>
                <p className="mt-4 text-[11px] text-center text-slate-400 font-mono">
                    Watford General Hospital · ED Whiteboard v3.0
                </p>
            </div>
        </div>
    );
}
